import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import i18n from '../locales/i18n';

const LanguageContext = createContext();

// Fallback translations for errors only
const fallbackTranslations = {
  en: {
    errors: {
      networkError: 'Please check your internet connection and try again.',
      technicalError: 'Oops! We are experiencing some technical difficulties. Please try again later.',
      noInternet: 'No Internet Connection',
      sorry: 'We\'re Sorry!',
      tryAgain: 'Try Again',
      refreshPage: 'Refresh Page'
    }
  },
  ar: {
    errors: {
      networkError: 'يرجى التحقق من اتصال الإنترنت والمحاولة مرة أخرى.',
      technicalError: 'عذراً! نواجه بعض المشكلات التقنية. يرجى المحاولة مرة أخرى لاحقاً.',
      noInternet: 'لا يوجد اتصال بالإنترنت',
      sorry: 'نحن آسفون!',
      tryAgain: 'حاول مرة أخرى',
      refreshPage: 'تحديث الصفحة'
    }
  }
};

const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('preferredLanguage') || 'en';
  });
  const [isAnimating, setIsAnimating] = useState(false);

  const updateDocumentTitle = useCallback((pageTitle, siteInfo) => {
    if (siteInfo?.title) {
      document.title = pageTitle ? `${siteInfo.title} | ${pageTitle}` : siteInfo.title;
    }
  }, []);

  useEffect(() => {
    //console.log("language");
    //console.log(language);
    i18n.changeLanguage(language);
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language === 'ar' ? 'ar' : 'en';
  }, [language]);

  const changeLanguage = useCallback((newLang) => {
    setLanguage(newLang);
    localStorage.setItem('preferredLanguage', newLang);
  }, []);

  const toggleLanguage = useCallback(() => {
    setIsAnimating(true);
    const newLang = language === 'en' ? 'ar' : 'en';
    
    const loadingScreen = document.createElement('div');
    loadingScreen.className = 'loading-screen';
    loadingScreen.innerHTML = '<div class="loader"></div>';
    document.body.appendChild(loadingScreen);

    // Change the language
    changeLanguage(newLang);

    // Remove loading screen after animation
    setTimeout(() => {
      document.body.removeChild(loadingScreen);
      setIsAnimating(false);
    }, 1000);
  }, [language, changeLanguage]);

  const t = useCallback((key) => {
    const keys = key.split('.');
    let value = fallbackTranslations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage: changeLanguage,
      toggleLanguage, 
      isAnimating, 
      t,
      updateDocumentTitle // Add this to the context
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export { LanguageProvider, useLanguage };